/**
 * The bid table as it is printed on an Australian 500 scorepad, laid on the
 * felt: levels down, strains across, the value in the cell, a pencil circle
 * around the one you are choosing.
 *
 * The *contents* are generated: a rung is live only if it appears in
 * `actions.legalCalls`, so "you cannot bid under the current high bid" is dead
 * print rather than an error path, and no rule is re-derived here. The grid
 * itself is layout, which is what a per-game `BidLayout` is for.
 */
import type { BidLayoutProps, LegalCall } from '../types'
import { Strainy } from '../../table/atoms'
import {
  LEVELS,
  MISERE_VALUE,
  OPEN_MISERE_VALUE,
  STRAINS,
  ladderValue,
  readAuctionCalls,
  readSpec,
  type Spec,
} from './spec'

function specKey(spec: Spec): string {
  return spec.kind === 'suit' ? `${spec.level}${spec.strain}` : spec.kind
}

function callSpec(call: LegalCall): Spec | null {
  return readSpec(call.call) ?? readSpec(call.label)
}

export function BidLadder({ view, calls, selected, onSelect }: BidLayoutProps): JSX.Element {
  const live = new Map<string, LegalCall>()
  for (const call of calls) {
    const spec = callSpec(call)
    if (spec) live.set(specKey(spec), call)
  }

  // A standing bid is written under its rung with the bidder's name, the way a
  // scorer pencils it on the pad.
  const standing = new Map<string, string>()
  for (const { seat, call } of readAuctionCalls(view.hand?.auction)) {
    const spec = readSpec(call)
    if (spec) standing.set(specKey(spec), view.name(seat))
  }

  const selectedKey = selected ? (callSpec(selected) ? specKey(callSpec(selected)!) : null) : null

  const cell = (key: string, value: number, extra?: string) => {
    const call = live.get(key)
    const dead = !call
    const sel = selectedKey === key
    const who = standing.get(key)
    return (
      <button
        key={key}
        type="button"
        className={`cell${dead ? ' dead' : ''}${sel ? ' sel' : ''}${extra ? ` ${extra}` : ''}`}
        disabled={dead}
        onClick={() => onSelect(sel ? null : (call ?? null))}
        aria-pressed={sel}
      >
        {value}
        {who ? <small>{who}</small> : null}
      </button>
    )
  }

  const misere = live.get('misere')
  const openMisere = live.get('openMisere')

  return (
    <div>
      <div className="ladder">
        <div className="hd" />
        {STRAINS.map((strain) => (
          <div key={strain} className={strain === 'NT' ? 'hd nt' : 'hd'}>
            <Strainy strain={strain} />
          </div>
        ))}
        {LEVELS.map((level) => (
          <Row key={level} level={level}>
            {STRAINS.map((strain) => cell(`${level}${strain}`, ladderValue(level, strain)))}
          </Row>
        ))}
      </div>
      <div className="ladder-misere">
        <SpecialCell
          label="Misère"
          value={MISERE_VALUE}
          call={misere}
          selected={selectedKey === 'misere'}
          onSelect={onSelect}
        />
        <SpecialCell
          label="Open misère"
          value={OPEN_MISERE_VALUE}
          call={openMisere}
          selected={selectedKey === 'openMisere'}
          onSelect={onSelect}
        />
      </div>
    </div>
  )
}

/** The grid is one flat CSS grid, so a "row" is just the level gutter plus its
 *  five cells emitted in order. */
function Row({ level, children }: { level: number; children: React.ReactNode }): JSX.Element {
  return (
    <>
      <div className="lv">{level}</div>
      {children}
    </>
  )
}

function SpecialCell({
  label,
  value,
  call,
  selected,
  onSelect,
}: {
  label: string
  value: number
  call: LegalCall | undefined
  selected: boolean
  onSelect: (call: LegalCall | null) => void
}): JSX.Element {
  return (
    <button
      type="button"
      className={`cell${call ? '' : ' dead'}${selected ? ' sel' : ''}`}
      disabled={!call}
      onClick={() => onSelect(selected ? null : (call ?? null))}
      aria-pressed={selected}
    >
      {label} <small>{value}</small>
    </button>
  )
}
